import { isEqual } from 'lodash-es'
import { acceptHMRUpdate, defineStore } from 'pinia'

import { type RiskConfig } from '~/types.ts'

function createEmptyProfile(): RiskConfig {
  return {
    id: 0,
    organization_id: 0,
    country_profile: {
      name: '',
      nullWeight: 60,
      countries: {},
    },
    threshold: 0,
    created_at: '',
    updated_at: '',
  }
}

export const useRiskConfig = defineStore('riskConfig', () => {
  const config = reactive<RiskConfig>(createEmptyProfile())
  const saved = reactive<RiskConfig>(createEmptyProfile())
  const defaults = reactive<RiskConfig>(createEmptyProfile())

  async function load() {
    if (stores.loading.riskConfig) return
    await stores.loading.update({ riskConfig: true })

    try {
      const { data } = await api.get(`risk-configs/${stores.organization.id}`)

      Object.assign(config, data)
      Object.assign(saved, copyObject(data))

      const { data: newDefaults } = await api.get('risk-config-defaults')
      Object.assign(defaults, newDefaults)
    } finally {
      await stores.loading.update({ riskConfig: false })
    }
  }

  async function save(onChangeAction: string | null) {
    await stores.loading.update({ riskConfig: true })

    try {
      const { data } = await api.patch(`risk-configs/${config.id}`, { config, on_change: onChangeAction })
      Object.assign(saved, copyObject(data))
    } finally {
      await stores.loading.update({ riskConfig: false })
    }
  }

  function profileName(profile?: RiskConfig['country_profile']) {
    if (!profile) {
      profile = config.country_profile
    }

    if (isEqual(profile, defaults.country_profile)) {
      return $t('default-name', { name: `(${profile.name})` })
    }

    return $t('custom-profile-based-on-original', { original: profile.name })
  }

  return {
    config,
    saved,
    defaults,
    profileName,
    load,
    save,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRiskConfig, import.meta.hot))
}
